import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromEvent, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  CategoriesCheckDefaultTeamExists,
  Category,
  CategoryService,
  selectCategoriesDefaultTeamExists,
  TeamMemberService,
} from '../../../../../../core/lists';
import { AuthService, currentUser, UserLoaded } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { FaxEmailService } from '../../../../../../email-and-fax/services/fax-email.service';
import { ThrowCategoryError } from '../../../../../../core/lists/_actions/category.actions';
import { LayoutUtility } from '../../../../../../shared/utils';

@Component({
  selector: 'kt-category-add',
  templateUrl: './category-add.dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryAddDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  category: Category;
  categoryForm: FormGroup;
  hasFormErrors: boolean;
  disableDefaultTeam: boolean;
  companyId: number;
  private subscriptions: Subscription[] = [];
  searchInboxFn = this.faxEmailService.filterInboxList.bind(this.faxEmailService);
  @ViewChild('searchInboxAutocomplete', { static: true })
  searchInboxAutocomplete: GeneralAutocompleteComponent;
  inboxCtrl = new FormControl(null);
  searchRolesFn = this.authService.searchRoles.bind(this.authService);
  roleCtrl = new FormControl([]);
  @ViewChild('roleAutocomplete') roleAutocomplete: GeneralAutocompleteComponent;
  formatNewRoleFn = ((value: any) => {
    return value;
  }).bind(this);
  formatNewInboxFn = ((value: any) => {
    return value;
  }).bind(this);
  selectedManagers = [];
  managers = [];
  loading = false;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  @ViewChild('auto', { static: false }) matAutocomplete: MatAutocomplete;
  @ViewChild('managerInput', { static: false }) managersInput: ElementRef<HTMLInputElement>;
  areRolesEmpty = true;
  duplicateError = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private teamMember: TeamMemberService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CategoryAddDialogComponent>,
    private store: Store<AppState>,
    private categoryFB: FormBuilder,
    private translate: TranslateService,
    public dialog: MatDialog,
    private authService: AuthService,
    private faxEmailService: FaxEmailService,
    private categoryService: CategoryService,
    private layoutUtility: LayoutUtility,
  ) {}

  ngOnInit() {
    this.store.dispatch(new CategoriesCheckDefaultTeamExists());
    this.category = this.data && this.data.category ? this.data.category : new Category();
    this.selectedManagers = this.data && this.data.managers ? this.data.managers : [];
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res !== undefined ? res.companyID : -1;
    });
    this.subscriptions.push(userSub);
    this.subscriptions.push(
      this.store.pipe(select(selectCategoriesDefaultTeamExists)).subscribe((res) => {
        this.disableDefaultTeam =
          res !== undefined ? res === true && !this.category.is_default_intervention_team : false;
      }),
    );
    this.createForm();
  }

  getTitle() {
    return this.category.id > 0
      ? this.translate.instant('CATEGORY.EDIT_CATEGORY')
      : this.translate.instant('CATEGORY.ADD_CATEGORY');
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    if (this.managersInput) {
      fromEvent(this.managersInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(600),
          distinctUntilChanged(),
          tap(() => {
            this.loadManagers();
          }),
        )
        .subscribe();
    }
  }

  onSubmit() {
    this.hasFormErrors = false;
    this.duplicateError = false;
    const controls = this.categoryForm.controls;
    this.roleAutocomplete.updateValueAndValidity();
    /** check form */
    if (this.categoryForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    if (this.category.id > 0) {
      this.editCategory();
    } else {
      this.createCategory();
    }
  }

  createCategory() {
    const fax_number = this.categoryForm.get('fax_number').value;
    const body: any = {
      name: this.categoryForm.get('name').value,
      roles: this.categoryForm.get('roles').value,
      email: this.categoryForm.get('email').value,
      fax_number: fax_number ? fax_number.replace(/\D/g, '') : fax_number,
      inbox_id: this.categoryForm.get('inbox').value?.id,
      has_intervention_permission: this.categoryForm.get('hasInterventionPermission').value,
      is_default_intervention_team: this.categoryForm.get('isDefaultInterventionTeam').value,
      is_active: this.categoryForm.get('isActive').value,
    };
    this.categoryService.createCategory(body).subscribe(
      () => {
        this.layoutUtility.showActionNotification('SUCCESS', 'Team created successfully');
        this.close({ created: true });
        setTimeout(() => this.loadUser(), 1500);
      },
      (err) => {
        if (err.error?.code === 409) {
          this.duplicateError = true;
          this.cdr.detectChanges();
        } else return new ThrowCategoryError({ error: err.error });
      },
    );
  }

  editCategory() {
    const controls = this.categoryForm.controls;
    const _category = new Category();
    _category.company_id = this.companyId;
    _category.id = this.category.id;
    _category.name = controls.name.value;
    _category.roles = controls.roles.value;
    _category.inbox_id = controls.inbox?.value ? controls.inbox.value.id : null;
    _category.managers = controls.managerControl.value;
    _category.email = controls.email.value;
    _category.fax_number =
      controls.fax_number.value !== null
        ? controls.fax_number.value.replace(/\D/g, '')
        : controls.fax_number.value;
    _category.has_intervention_permission = controls.hasInterventionPermission.value;
    _category.is_default_intervention_team = controls.isDefaultInterventionTeam.value;
    _category.is_active = controls.isActive.value;
    for (const [key, value] of Object.entries(_category)) {
      if (typeof value === 'string') {
        _category[key] = value.trim();
      }
    }

    this.categoryService.updateCategory(_category).subscribe(
      () => {
        this.layoutUtility.showActionNotification('SUCCESS', 'Team updated successfully');
        this.close({ updated: true });
        setTimeout(() => this.loadUser(), 1500);
      },
      (err) => {
        if (err.error?.code === 409) {
          this.duplicateError = true;
          this.cdr.detectChanges();
        } else return new ThrowCategoryError({ error: err.error });
      },
    );
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.categoryForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  close(result?: any) {
    this.dialogRef.close(result);
  }

  createForm() {
    this.categoryForm = this.categoryFB.group({
      name: [this.category.name, Validators.required],
      roles: [],
      email: [this.category.email, Validators.email],
      fax_number: [
        this.category.fax_number
          ? '(' +
            this.category.fax_number.substring(0, 3) +
            ') ' +
            this.category.fax_number.substring(3, 6) +
            '-' +
            this.category.fax_number.substring(6, 10)
          : '',
        Validators.pattern(/^\(([0-9]{3})\) ([0-9]{3})-([0-9]{4})$/),
      ],
      inbox: [this.category.inbox_id],
      hasInterventionPermission: [this.category.has_intervention_permission],
      isDefaultInterventionTeam: [
        { value: this.category.is_default_intervention_team, disabled: this.disableDefaultTeam },
      ],
      isActive: [this.category.is_active ?? true],
      managerControl: [this.selectedManagers],
    });
    if (this.category?.id) {
      const newParticipant = this.category.roles.map((value: { id: any; name: any }) => {
        return {
          id: value.id,
          name: value.name,
        };
      });
      this.categoryForm.get('roles').setValue(newParticipant);
      this.roleCtrl.setValue(newParticipant);
      this.categoryForm.get('inbox').setValue(this.category.inbox);
      this.inboxCtrl.setValue(this.category.inbox);
      this.searchInboxAutocomplete.setValue(this.category.inbox);
      this.searchInboxAutocomplete.updateValueAndValidity();
      this.areRolesEmpty = this.categoryForm.get('roles')?.value?.length === 0;
      this.loadManagers();
    }
  }

  selectRole(val: any[]): void {
    const newParticipant = val.map((value) => {
      return {
        id: value.id,
        name: value.name,
      };
    });
    this.categoryForm.get('roles').setValue(newParticipant);
    this.loadManagers();
    this.areRolesEmpty = this.categoryForm.get('roles')?.value?.length === 0;
    if (this.areRolesEmpty) {
      this.selectedManagers = [];
      this.categoryForm.get('managerControl').setValue(this.selectedManagers);
    }
  }

  loadManagers() {
    this.loading = true;
    this.managers = [];
    this.teamMember
      .teamManagersAutocomplete(
        this.categoryForm?.get('roles').value,
        this.managersInput?.nativeElement?.value || '',
      )
      .subscribe((res: any) => {
        this.managers = res.data;
        this.selectedManagers.map((e) => {
          const u = this.managers?.find((ele) => ele.id === e.id);
          if (u) {
            const index = this.managers.indexOf(u);
            this.managers.splice(index, 1);
          }
        });
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  selectInbox(val: any) {
    this.categoryForm.get('inbox').setValue(val);
    this.inboxCtrl.setValue(val);
  }

  filterRoleFn(event: any): string {
    return event ? event.name : '';
  }

  selectManager(val: { option: { value: any } }): void {
    this.managersInput.nativeElement.value = '';
    const res = val.option.value;
    res.is_manager = this.selectedManagers.length === 0;
    this.selectedManagers.push(res);
    this.categoryForm.get('managerControl').setValue(this.selectedManagers);
  }

  removeManager(state: any): void {
    const index = this.selectedManagers.indexOf(state);
    if (index >= 0) {
      this.selectedManagers.splice(index, 1);
    }
    for (let i = 0; i < this.selectedManagers.length; i++) {
      this.selectedManagers[i].number = (i + 1).toString();
    }
    this.categoryForm.get('managerControl').setValue(this.selectedManagers);
  }

  markAsPrimaryManager(m: any) {
    const index1 = this.selectedManagers.indexOf(m);
    this.selectedManagers = this.selectedManagers.map((e) => {
      e.is_manager = false;
      return e;
    });
    this.selectedManagers[index1].is_manager = true;
  }

  loadUser() {
    this.authService.getUserByToken().subscribe((user) => {
      if (user) {
        this.store.dispatch(new UserLoaded({ user }));
      }
    });
  }

  isASelectedManager(p: any) {
    return this.selectedManagers?.indexOf(p) !== -1;
  }

  onAlertClose() {
    this.duplicateError = false;
  }
}
